import { ChannelsAppsSidebarList_NavSectionPaginationFragment$key } from "@/chat/sidebar/channels/__generated__/ChannelsAppsSidebarList_NavSectionPaginationFragment.graphql"
import { ChannelsAppsSidebarList_NavSectionPaginationQuery } from "@/chat/sidebar/channels/__generated__/ChannelsAppsSidebarList_NavSectionPaginationQuery.graphql"
import { ChannelsAppsSidebarList_OrganizationPaginationFragment$key } from "@/chat/sidebar/channels/__generated__/ChannelsAppsSidebarList_OrganizationPaginationFragment.graphql"
import { ChannelsAppsSidebarList_OrganizationPaginationQuery } from "@/chat/sidebar/channels/__generated__/ChannelsAppsSidebarList_OrganizationPaginationQuery.graphql"
import ChatChannelSideBarItem from "@/product/sidebar/chat/channels/ChatChannelSideBarItem"
import RenderIfVisible from "@/product/sidebar/RenderIfVisible"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoSideBarItemSkeleton } from "@disco-ui"
import DiscoScrolledIntoView from "@disco-ui/scrolled-into-view/DiscoScrolledIntoView"
import { range } from "@utils/array/arrayUtils"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import { TestIDProps } from "@utils/typeUtils"
import { graphql, usePaginationFragment } from "react-relay"

type Props = TestIDProps & {
  topLevelKey?: ChannelsAppsSidebarList_OrganizationPaginationFragment$key | null
  navSectionKey?: ChannelsAppsSidebarList_NavSectionPaginationFragment$key | null
}

const APPS_PER_PAGE = 10

export default function ChannelsAppsSidebarList(props: Props) {
  const { topLevelKey, navSectionKey, testid = "ChannelsAppsSidebarList" } = props
  const classes = useStyles()
  const { virtualizeProductsSidebar } = useFeatureFlags()

  // Top level apps with their pagination
  const topLevelPagination = usePaginationFragment<
    ChannelsAppsSidebarList_OrganizationPaginationQuery,
    ChannelsAppsSidebarList_OrganizationPaginationFragment$key
  >(
    graphql`
      fragment ChannelsAppsSidebarList_OrganizationPaginationFragment on Query
      @refetchable(queryName: "ChannelsAppsSidebarList_OrganizationPaginationQuery")
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 10 }
        after: { type: "String" }
      ) {
        topLevelApps: channelsSidebarTopLevelApps(first: $first, after: $after)
          @connection(
            key: "ChannelsAppsSidebarList_OrganizationPaginationFragment__topLevelApps"
          ) {
          edges {
            node {
              id
              ...ChatChannelSideBarItemFragment
            }
          }
        }
      }
    `,
    topLevelKey || null
  )
  const organization = topLevelPagination?.data

  // Nav section level apps with their pagination
  const navSectionPagination = usePaginationFragment<
    ChannelsAppsSidebarList_NavSectionPaginationQuery,
    ChannelsAppsSidebarList_NavSectionPaginationFragment$key
  >(
    graphql`
      fragment ChannelsAppsSidebarList_NavSectionPaginationFragment on NavSection
      @refetchable(queryName: "ChannelsAppsSidebarList_NavSectionPaginationQuery")
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 10 }
        after: { type: "String" }
      ) {
        id
        apps: channelsSidebarNavSectionApps(first: $first, after: $after)
          @connection(key: "ChannelsAppsSidebarList_NavSectionPaginationFragment__apps") {
          edges {
            node {
              id
              ...ChatChannelSideBarItemFragment
            }
          }
        }
      }
    `,
    navSectionKey || null
  )
  const navSection = navSectionPagination?.data

  // Use either the organization or nav section fragment's pagination + data
  const { loadNext, hasNext, isLoadingNext } = organization
    ? topLevelPagination
    : navSectionPagination
  const apps = organization
    ? Relay.connectionToArray(organization.topLevelApps)
    : Relay.connectionToArray(navSection?.apps)

  if (!apps.length) return null

  return (
    <div className={classes.list} data-testid={testid}>
      {apps.map((app) => {
        const content = (
          <ChatChannelSideBarItem
            key={app.id}
            testid={"ChannelsSidebar.community-top-level-channels"}
            appKey={app}
            isChannelsSidebar
          />
        )

        if (virtualizeProductsSidebar) {
          return <RenderIfVisible key={app.id}>{content}</RenderIfVisible>
        }

        return content
      })}

      {hasNext && (
        <DiscoScrolledIntoView
          onScrolledIntoView={() => loadNext(APPS_PER_PAGE)}
          isLoading={isLoadingNext}
          skeleton={
            <>
              {range(APPS_PER_PAGE).map((k) => (
                <DiscoSideBarItemSkeleton key={k} />
              ))}
            </>
          }
        />
      )}
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  list: {
    padding: theme.spacing(0, 2),
  },
}))
